import { purchaseService } from '@/features/payment/api/purchase';
import {
  AnalyticsEvent,
  trackEvent,
  type PurchaseCheckoutOutcome,
  type PurchaseProductKind,
} from '@/shared/lib/analytics';

/**
 * One reporting point for every way a checkout can end.
 *
 * WHY THIS EXISTS: the money path emitted `*_purchase_started` and
 * `*_purchase_completed` and nothing in between, so a checkout that produced no
 * sale left no trace. A 2026-08-01 funnel audit found 15 pack checkouts started
 * and 1 completed, with the other 14 unattributable — an abandoned overlay, a
 * declined card (buyer WANTED to pay and could not) and a product that never
 * loaded were indistinguishable silence. Those are different problems and only
 * some of them are our bug.
 *
 * Reports go to two places on purpose: GA4 for funnel analysis, and our own API
 * so the purchase watchdog can alert on it (GA4 cannot raise an alert and is
 * not queryable from the server).
 *
 * CROSS-PLATFORM CONTRACT: mirrors Apple's `PurchaseTelemetry.swift` and
 * Android's `PurchaseTelemetry.kt`, down to the event and outcome strings.
 */
export function reportCheckoutOutcome(
  outcome: PurchaseCheckoutOutcome,
  kind: PurchaseProductKind,
  detail: string | undefined,
  correlationToken: string | undefined,
): void {
  trackEvent(AnalyticsEvent.PURCHASE_FAILED, {
    product_kind: kind,
    outcome,
    reason: detail ?? 'unknown',
  });

  if (!correlationToken) {
    return;
  }

  // Deliberately not awaited: the buyer is mid-flow (often mid-unmount) and a
  // telemetry write must never delay the UI or surface its own error on top of
  // whatever just failed. `resolveIntent` swallows its errors internally.
  void purchaseService.resolveIntent(correlationToken, outcome, detail);
}
